import type { ReactNode } from 'react';
import type { LookupResponse } from '../types';
import { HudFrame } from './HudFrame';

function Val({ children, tone }: { children: ReactNode; tone?: 'ok' | 'warn' | 'bad' | 'dim' }) {
  return <span className={`sig__val ${tone ? `sig__val--${tone}` : ''}`}>{children}</span>;
}

function boolTone(v: unknown): 'ok' | 'bad' | 'dim' {
  if (v === true) return 'ok';
  if (v === false) return 'bad';
  return 'dim';
}

export function SignalGrid({
  result,
  onRerun,
  rerunBusy,
}: {
  result: LookupResponse | null;
  onRerun?: (phone: string) => void;
  rerunBusy?: boolean;
}) {
  if (!result) {
    return (
      <HudFrame title="SIGNALS" code="SIG-00" status="idle" meta="AWAITING INPUT">
        <p className="empty">Submit a number to aggregate multi-source intelligence.</p>
      </HudFrame>
    );
  }

  const p = result.phone;
  const s = result.signals;
  const geo = (s.geographic_assignment ?? {}) as Record<string, unknown>;
  const rep = (s.reputation ?? {}) as Record<string, unknown>;
  const id = (s.identity_attribution ?? {}) as Record<string, unknown>;
  const valid = s.valid_consensus as { yes: number; no: number; total: number } | null;
  const phoneForRerun = p.normalized_e164 || p.raw_input;
  const archived = Boolean(result.archived);

  return (
    <HudFrame
      title="SIGNALS"
      code={archived ? 'SIG-LOG' : 'SIG-01'}
      status={archived ? 'stale' : 'live'}
      meta={`${result.durationMs}ms · ${result.requestId.slice(0, 8)}${archived ? ' · ARCHIVED' : ''}`}
      actions={
        onRerun && phoneForRerun ? (
          <button
            type="button"
            className="btn btn--tiny"
            disabled={rerunBusy}
            onClick={() => onRerun(phoneForRerun)}
            title="Run a fresh multi-source lookup as a new request"
          >
            {rerunBusy ? 'RUNNING…' : 'RE-RUN'}
          </button>
        ) : undefined
      }
    >
      <div className="hero">
        <div>
          <p className="hero__e164">{p.normalized_e164 ?? p.raw_input}</p>
          <p className="hero__sub">
            {p.international_format ?? p.national_format ?? '—'}
            {p.region_code ? ` · ${p.region_code}` : ''}
            {p.type_label ? ` · ${p.type_label}` : ''}
          </p>
          {archived && (
            <p className="hero__archive">
              Viewing stored log · id {result.requestId.slice(0, 12)} — not a live refresh
            </p>
          )}
        </div>
        <div className="hero__flags">
          {archived && <span className="chip chip--warn">FROM LOG</span>}
          <span className={`chip chip--${p.parseable ? 'ok' : 'bad'}`}>
            {p.parseable ? 'PARSEABLE' : 'UNPARSEABLE'}
          </span>
          <span className={`chip chip--${p.possible ? 'ok' : 'bad'}`}>
            {p.possible ? 'POSSIBLE' : 'IMPOSSIBLE'}
          </span>
          <span className={`chip chip--${p.valid_number_pattern ? 'ok' : 'warn'}`}>
            {p.valid_number_pattern ? 'VALID PATTERN' : 'INVALID PATTERN'}
          </span>
          {p.reserved_fictitious && <span className="chip chip--warn">FICTITIOUS 555</span>}
        </div>
      </div>

      <div className="metrics">
        <div className="metric">
          <span className="metric__k">Line type</span>
          <Val>{String(s.line_type ?? '—')}</Val>
        </div>
        <div className="metric">
          <span className="metric__k">Current carrier</span>
          <Val>{String(s.current_carrier ?? '—')}</Val>
        </div>
        <div className="metric">
          <span className="metric__k">Allocation provider</span>
          <Val tone="dim">{String(s.allocation_provider ?? '—')}</Val>
        </div>
        <div className="metric">
          <span className="metric__k">Rate center</span>
          <Val tone="dim">{String(s.rate_center ?? '—')}</Val>
        </div>
        <div className="metric">
          <span className="metric__k">CNAM / name</span>
          <Val>{String(s.cnam ?? id.associated_name ?? '—')}</Val>
        </div>
        <div className="metric">
          <span className="metric__k">Valid consensus</span>
          <Val tone={valid && valid.yes > valid.no ? 'ok' : valid ? 'warn' : 'dim'}>
            {valid ? `${valid.yes}/${valid.total} yes` : '—'}
          </Val>
        </div>
        <div className="metric">
          <span className="metric__k">NPA-NXX-X</span>
          <Val tone="dim">
            {p.npa && p.nxx ? `${p.npa}-${p.nxx}-${p.block ?? '?'}` : '—'}
          </Val>
        </div>
        <div className="metric">
          <span className="metric__k">Geography (allocation)</span>
          <Val tone="dim">{String(geo.location ?? geo.country ?? '—')}</Val>
        </div>
        <div className="metric">
          <span className="metric__k">FCC complaints</span>
          <Val tone={Number(rep.fcc_complaint_count) > 0 ? 'warn' : 'ok'}>
            {String(rep.fcc_complaint_count ?? 0)}
            {rep.fcc_recent_count != null ? ` (${rep.fcc_recent_count} recent)` : ''}
          </Val>
        </div>
        <div className="metric">
          <span className="metric__k">Fraud score</span>
          <Val
            tone={
              typeof rep.fraud_score === 'number'
                ? rep.fraud_score >= 75
                  ? 'bad'
                  : rep.fraud_score >= 40
                    ? 'warn'
                    : 'ok'
                : 'dim'
            }
          >
            {rep.fraud_score != null ? String(rep.fraud_score) : '—'}
          </Val>
        </div>
        <div className="metric">
          <span className="metric__k">Ported</span>
          <Val tone={boolTone(s.ported)}>{s.ported === true ? 'YES' : s.ported === false ? 'NO' : '—'}</Val>
        </div>
        <div className="metric">
          <span className="metric__k">Physical location</span>
          <Val tone="dim">N/A</Val>
        </div>
      </div>

      <p className="footnote">
        Prefix/carrier metadata is allocation-oriented; portable numbers may have a different current
        carrier. FCC/reputation signals are heuristics — not scam verdicts.
      </p>
    </HudFrame>
  );
}
